import { Component, ReactNode } from 'react';
import { reportClientError } from '../lib/clientErrorReporter';

/**
 * Catches render-time exceptions in the page content so a single thrown error
 * shows a recoverable message instead of a blank white screen. Mounted around
 * the routed <Outlet> in Layout, so the sidebar/header stay usable and only the
 * content area shows the error.
 *
 * The error text is shown so the user can report it (and so we can diagnose
 * without needing the browser console).
 */

interface Props {
  children: ReactNode;
  /** Changing this value resets the boundary (e.g. pass the route path so
   *  navigating away from a broken page clears the error). */
  resetKey?: string;
  /** Label for a card-scoped boundary (e.g. "Workflow Sankey"). When set, the
   *  boundary renders a compact inline error in place of the full-page UI so a
   *  single broken card doesn't take down the rest of the surrounding section. */
  cardLabel?: string;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    // Reset when the reset key changes (route navigation) so a fixed page renders.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Surface for diagnosis. (Replace with a logging service when one exists.)
    console.error('[ErrorBoundary] page render crashed:', error, info.componentStack);
    // Beacon to the team inbox via /api/client-error — best-effort, deduped
    // server-side. Component stack is appended to the JS stack so the email
    // shows both call-sites + the React tree.
    reportClientError({
      message: error.message || 'render crash',
      stack: `${error.stack || ''}\n--- componentStack ---${info.componentStack}`,
      source: 'ErrorBoundary',
    });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // Compact inline error for card-scoped boundaries — keeps the rest of the
    // surrounding section rendering. Used on the Reports tab so a crash in one
    // card (e.g. recharts choking on bad data) doesn't blank out the page.
    if (this.props.cardLabel) {
      return (
        <section className="bg-card rounded-lg border border-amber-200 p-5">
          <h3 className="text-base font-semibold text-ink mb-1">{this.props.cardLabel}</h3>
          <p className="text-sm text-amber-800">
            This card hit an unexpected error and couldn't render. The rest of the page still works.
          </p>
          <details className="mt-2">
            <summary className="text-[11px] font-semibold text-ink-faint cursor-pointer">Technical details</summary>
            <pre className="mt-1 text-[11px] text-red-700 whitespace-pre-wrap break-words">{error.message}</pre>
          </details>
        </section>
      );
    }

    return (
      <div className="max-w-xl mx-auto mt-12 text-center">
        <div className="text-5xl mb-4">⚠️</div>
        <h1 className="text-xl font-bold text-ink mb-2">Something went wrong on this page</h1>
        <p className="text-sm text-ink-muted mb-5">
          This page hit an unexpected error and couldn't finish loading. Your data is safe — the rest
          of the app still works. Try reloading, or head back to the dashboard.
        </p>
        <div className="flex items-center justify-center gap-3 mb-6">
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-2 bg-brand-primary text-white text-sm font-semibold rounded-lg hover:bg-brand-primary-dark transition-colors"
          >
            Reload page
          </button>
          <a
            href="/capacity"
            className="px-5 py-2 border border-line text-ink-muted text-sm font-semibold rounded-lg hover:bg-wash transition-colors"
          >
            Go to Capacity
          </a>
        </div>
        <details className="text-left bg-wash border border-line rounded-lg p-3">
          <summary className="text-xs font-semibold text-ink-faint cursor-pointer">
            Technical details (for support)
          </summary>
          <pre className="mt-2 text-xs text-red-700 whitespace-pre-wrap break-words">{error.message}</pre>
        </details>
      </div>
    );
  }
}
